import React, { useState, useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import Select, { components } from 'react-select';

import { API_URL } from '../reusable/url';
import './cityStateZipComponent.css';

const fetchCitiesByState = async (stateAbbr, searchInput) => {
  try {
    const endpoint = `getCities?stateAbbr=${stateAbbr}&searchInput=${encodeURIComponent(
      searchInput
    )}`;
    const url = API_URL(endpoint);
    const response = await fetch(url);

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Error ${response.status}: ${errorData}`);
    }

    const cities = await response.json();
    return cities;
  } catch (error) {
    console.error('Error fetching cities:', error);
    return [];
  }
};
/**
 * Full list of US states + DC
 */
const stateOptions = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];


const CityStateZipComponent = ({ control, setValue, errors, isSubmitted }) => {

  const [stateFocused, setStateFocused] = useState(false);
  const [cityFocused, setCityFocused] = useState(false);
  // Watch the 'state' field to enable/disable city
  const stateValue = useWatch({ control, name: 'state' }) || '';

  /**
   * Reset city whenever the user picks a new state
   */
  useEffect(() => {
    setValue('city', '', { shouldValidate: true });
  }, [stateValue, setValue]);

  /**
   * Loader function for city async dropdown
   */
  const loadCityOptions = async (inputValue, _, { stateParam }) => {
    if (!stateParam || !inputValue) {
      return { options: [] };
    }
    const cities = await fetchCitiesByState(stateParam, inputValue);
    return {
      options: cities.map((c) => ({ label: c, value: c })),
    };
  };

  /**
   * Optional custom dropdown indicator
   */
  const DropdownIndicator = (props) => {
    const { selectProps } = props;
    if (selectProps.menuIsOpen) {
      return <components.DropdownIndicator {...props} />;
    }
    return null;
  };

  /**
   * Custom styling for both select boxes
   */
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: state.isFocused ? '1px solid green' : '1px solid #36699f',
      backgroundColor: state.isFocused ? 'blanchedalmond' : 'whitesmoke',
      minHeight: '40px',
      height: '40px',
      boxShadow: 'none',
      padding: 0,
      boxSizing: 'border-box',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 10px',
      margin: '0',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'navy',
      fontSize: '16px',
      margin: 0,
      paddingLeft: '10px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'navy',
      fontSize: '16px',
      margin: 0,
      paddingLeft: '20px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorsContainer: (provided) => ({
      ...provided,
      color: 'navy',
      padding: '5px',
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: '150px',
      zIndex: 9999,
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      boxSizing: 'border-box',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '150px',
    }),
  };

  return (
    <>
      {/* STATE CONTROLLER */}
      <Controller
        name="state"
        control={control}
        rules={{ required: 'State is required' }}
        render={({ field, fieldState: { error } }) => {
          const hasValue = !!field.value;
          const currentOption = stateOptions.find(
            (opt) => opt.value === field.value
          );
          return (
            <div id="state-field" className="city-state-zip-container">
              <div className={field.value ? 'has-value' : ''}>
                <Select
                  value={currentOption || null}
                  onChange={(option) => field.onChange(option?.value)}
                  onFocus={() => setStateFocused(true)}
                  onBlur={(e) => {
                    field.onBlur(e);
                    setStateFocused(false);
                  }}
                  placeholder=" "
                  options={stateOptions}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={customSelectStyles}
                  isSearchable
                  components={{ DropdownIndicator }}
                  menuPlacement="bottom"
                  menuPosition="absolute"
                />
                <label className={(hasValue || stateFocused) ? 'active' : ''}>
                  State
                </label>

                {/* Show validation errors */}
                {(error || errors?.state) && (
                  <p style={{ color: 'red', marginTop: '2px' }}>
                    {error?.message || errors?.state?.message}
                  </p>
                )}
              </div>
            </div>
          );
        }}
      />

      {/* CITY CONTROLLER */}
      <Controller
        name="city"
        control={control}
        rules={{ required: 'City is required' }}
        render={({ field, fieldState: { error } }) => {
          const hasValue = !!field.value;
          const cityOption = field.value
            ? { label: field.value, value: field.value }
            : null;

          return (
            <div id="city-field" className="city-state-zip-container">
              <div className={field.value ? 'has-value' : ''}>
                <AsyncPaginate
                  value={cityOption}
                  loadOptions={loadCityOptions}
                  onChange={(option) => {
                    field.onChange(option ? option.value : '');
                  }}
                  onFocus={() => setCityFocused(true)}
                  onBlur={(e) => {
                    field.onBlur(e);
                    setCityFocused(false);
                  }}
                  placeholder=" "
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={customSelectStyles}
                  isDisabled={!stateValue} // can't pick city if no state chosen
                  additional={{ stateParam: stateValue }}
                  components={{ DropdownIndicator }}
                  menuPlacement="bottom"
                  menuPosition="absolute"
                />
                <label className={(hasValue || cityFocused) ? 'active' : ''}>
                  City
                </label>
                {(error || errors?.city) && isSubmitted && (
                  <p style={{ color: 'red', marginTop: '2px' }}>
                    {error?.message || errors?.city?.message}
                  </p>
                )}
              </div>
            </div>
          );
        }}
      />
    </>
  );
};

export default CityStateZipComponent;
